import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase-config';

function yesNo(v) {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return null;
}

function DetailRow({ label, children }) {
  if (children == null || children === '') return null;
  return (
    <div style={{ marginBottom: '12px' }}>
      <p style={{ margin: '0 0 4px 0', fontSize: '11px', fontWeight: 700, color: '#64748b', letterSpacing: '0.04em' }}>
        {label}
      </p>
      <div style={{ margin: 0, fontSize: '14px', color: '#0f172a', lineHeight: 1.45 }}>{children}</div>
    </div>
  );
}

/**
 * Owner / tenant: read-only popup with public-facing professional profile from `ProfessionalsData`.
 */
export default function ProfessionalProfilePeekModal({ professionalId, fallbackName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!professionalId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setData(null);
    getDoc(doc(db, 'ProfessionalsData', professionalId))
      .then((snap) => {
        if (cancelled) return;
        if (!snap.exists()) {
          setError('Profile not found.');
          return;
        }
        setData(snap.data() || {});
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'Could not load profile.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [professionalId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!professionalId) return null;

  const displayName =
    (data && (data.businessName || data.companyName)?.trim()) ||
    (fallbackName && String(fallbackName).trim()) ||
    'Professional';

  const trades = Array.isArray(data?.categories) && data.categories.length
    ? data.categories
    : Array.isArray(data?.selectedTrades) && data.selectedTrades.length
      ? data.selectedTrades
      : [];

  const q = data?.qualifications && typeof data.qualifications === 'object' ? data.qualifications : null;
  const licenseLine = q ? yesNo(q.hasLicense) : null;
  const insuranceLine = q ? yesNo(q.hasInsurance) : null;
  const refsLine = q ? yesNo(q.hasReferences) : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pro-peek-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100010,
        padding: '16px',
        overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '440px',
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 20px 50px rgba(15,23,42,0.2)',
          padding: '22px 22px 18px',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            border: 'none',
            background: 'transparent',
            fontSize: '22px',
            lineHeight: 1,
            cursor: 'pointer',
            color: '#64748b',
            padding: '4px 8px',
          }}
        >
          ✕
        </button>

        <h2 id="pro-peek-title" style={{ margin: '0 36px 6px 0', fontSize: '20px', color: '#0f172a', fontWeight: 700 }}>
          {loading ? 'Loading…' : displayName}
        </h2>
        <p style={{ margin: '0 0 18px 0', fontSize: '12px', color: '#94a3b8' }}>Profile details from their registration</p>

        {loading ? (
          <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Fetching professional profile…</p>
        ) : error ? (
          <p style={{ margin: 0, color: '#b91c1c', fontSize: '14px' }}>{error}</p>
        ) : (
          <div style={{ maxHeight: 'min(60vh, 420px)', overflowY: 'auto', paddingRight: '4px' }}>
            {data?.companyName &&
            data.companyName.trim() &&
            data.companyName.trim() !== (data.businessName || '').trim() ? (
              <DetailRow label="Company name">{data.companyName.trim()}</DetailRow>
            ) : null}
            <DetailRow label="Email">
              {data?.email ? (
                <a href={`mailto:${String(data.email).trim()}`} style={{ color: '#2563eb', fontWeight: 600 }}>
                  {data.email}
                </a>
              ) : (
                '—'
              )}
            </DetailRow>
            <DetailRow label="Phone">
              {data?.phoneNumber ? (
                <a href={`tel:${String(data.phoneNumber).replace(/\s/g, '')}`} style={{ color: '#2563eb', fontWeight: 600 }}>
                  {data.phoneNumber}
                </a>
              ) : (
                '—'
              )}
            </DetailRow>
            <DetailRow label="Primary service area">{data?.serviceArea || '—'}</DetailRow>
            <DetailRow label="Service radius">
              {data?.serviceRadius != null && data.serviceRadius !== ''
                ? `${data.serviceRadius} miles`
                : null}
            </DetailRow>
            <DetailRow label="Listed service fee">
              {data?.serviceFee != null && data.serviceFee !== '' && !Number.isNaN(Number(data.serviceFee))
                ? `$${Number(data.serviceFee).toFixed(2)}`
                : null}
            </DetailRow>
            {trades.length > 0 ? (
              <DetailRow label="Trades / categories">
                <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
                  {trades.map((t) => (
                    <li key={t} style={{ marginBottom: '4px' }}>
                      {t}
                    </li>
                  ))}
                </ul>
              </DetailRow>
            ) : null}

            {q ? (
              <>
                <p
                  style={{
                    margin: '16px 0 8px',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#64748b',
                    letterSpacing: '0.04em',
                  }}
                >
                  Qualifications
                </p>
                {licenseLine != null ? <DetailRow label="Licensed (as stated)">{licenseLine}</DetailRow> : null}
                {insuranceLine != null ? <DetailRow label="Insured (as stated)">{insuranceLine}</DetailRow> : null}
                {q.yearsExperience ? <DetailRow label="Years of experience">{q.yearsExperience}</DetailRow> : null}
                {q.specialties ? <DetailRow label="Specialties">{q.specialties}</DetailRow> : null}
                {refsLine != null ? <DetailRow label="References available">{refsLine}</DetailRow> : null}
                {q.referencesLink ? (
                  <DetailRow label="References / portfolio link">
                    <a href={q.referencesLink} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', wordBreak: 'break-all' }}>
                      {q.referencesLink}
                    </a>
                  </DetailRow>
                ) : null}
              </>
            ) : null}
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          style={{
            marginTop: '20px',
            width: '100%',
            padding: '11px',
            borderRadius: '8px',
            border: 'none',
            background: '#e2e8f0',
            color: '#334155',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}
